export enum CacheRoute {
	Shell = 'shell',
	Content = 'content',
	Launch = 'launch',
	Media = 'media',
	Probe = 'probe',
	Network = 'network',
}

interface RouterHosts {
	imagesHost: string
	uploadsHost: string
}

interface RoutedRequest {
	mode?: string
	method?: string
}

// Cache layout dependencies: Leaflet, the Font Awesome kit a project may still supply, and the Google
// Fonts a theme names. The theme stylesheet is precached but the faces it imports are not — they are
// cross-origin and unenumerable — so they are at least kept once fetched rather than lost on reload.
const SHELL_HOSTS = [
	'unpkg.com',
	'kit.fontawesome.com',
	'ka-f.fontawesome.com',
	'ka-p.fontawesome.com',
	'fonts.googleapis.com',
	'fonts.gstatic.com',
]

const DEPLOY_ASSET_PATH = /^\/web\/\d{14}\//
const THEME_PATH = /^\/\d+\/theme\.css$/

// Do not replace Rails-served file navigations with the cached app shell.
const FILE_PATH = /\.[a-z0-9]+$/i

// Cache only whole image and audio files, excluding video streams and range-based map archives.
const MEDIA_FILE = /\.(jpe?g|png|gif|webp|avif|svg|mp3|m4a|aac|ogg|oga|opus|wav)$/i

// These read-API timestamps change per boot rather than when content changes.
const CACHE_BUSTED_JSON = ['theme.json', 'manifest_full.json']

// The app metadata and manifest a launch cannot get past the ReadyGate without, from either read API.
const LAUNCH_JSON = ['app.json', 'manifest.json']
const LAUNCH_PATH = /^\/api\/v3\/app(?:\/manifest)?$/

/**
 * One versioned document from the direct API, which a project without the publishing model reads instead
 * of the published subtree. The version sits in the path rather than in a `?t=`, so these are immutable
 * for the same reason the published URLs are, and safe cache-first under the same rule.
 *
 * `items` and `triggers` are the two indexes that hang off a collection. Deliberately unmatched: the
 * `manifest` endpoints and `/api/v3/app`, which name the latest version rather than carrying one — going
 * cache-first on those would pin a tour to the version it was downloaded at and never see a republish.
 */
const DIRECT_CONTENT_PATH = /^\/api\/v3\/[a-z_]+\/\d+(?:\/(?:items|triggers))?\/[A-Za-z-]+\/\d+$/

// Match only our media hosts while allowing generateImageUrl to replace their region segment.
const REGION_SEGMENT = /[a-z]{2}-(images|uploads)/
const blankRegion = (host: string): string => host.replace(REGION_SEGMENT, '*-$1')

/** Route recognized reads to caches and leave every other URL to the network. */
export const createRouter = ({ imagesHost, uploadsHost }: RouterHosts) => {
	const mediaHosts = [imagesHost, uploadsHost].map(blankRegion)

	return (url: string, request: RoutedRequest): CacheRoute => {
		let parsed: URL
		try {
			parsed = new URL(url)
		} catch {
			return CacheRoute.Network
		}

		const { hostname, pathname, searchParams } = parsed
		const isMedia =
			mediaHosts.includes(blankRegion(hostname)) && pathname.startsWith('/uploads/') && MEDIA_FILE.test(pathname)

		// resolveAudioSource asks by HEAD whether a transformed mp3 exists, before every play. Left to the
		// network that question goes unanswered offline, and the source it resolves to instead is not the
		// one the download stored.
		if (request.method === 'HEAD') return isMedia ? CacheRoute.Probe : CacheRoute.Network

		if (request.method && request.method !== 'GET') return CacheRoute.Network

		// Demo navigation renders unpublished content, not the cached published shell.
		if (request.mode === 'navigate') {
			return searchParams.has('demo') || FILE_PATH.test(pathname) ? CacheRoute.Network : CacheRoute.Shell
		}

		if (DEPLOY_ASSET_PATH.test(pathname) || THEME_PATH.test(pathname) || SHELL_HOSTS.includes(hostname)) {
			return CacheRoute.Shell
		}

		if (isMedia) {
			return CacheRoute.Media
		}

		const file = pathname.slice(pathname.lastIndexOf('/') + 1)
		if (LAUNCH_JSON.includes(file) || LAUNCH_PATH.test(pathname)) {
			return CacheRoute.Launch
		}

		// Only versioned, immutable content JSON is safe for cache-first.
		if (file.endsWith('.json') && searchParams.has('t') && !CACHE_BUSTED_JSON.includes(file)) {
			return CacheRoute.Content
		}

		if (DIRECT_CONTENT_PATH.test(pathname)) {
			return CacheRoute.Content
		}

		return CacheRoute.Network
	}
}
